import React from 'react';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

import {
	useExtensionList as useExtensionListApollo,
	useExtensionListRelay,
} from '../../web-client/hooks/useExtensionList';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { Loader } from '../../web-runtime';

import { ForgeUIRenderer } from './main';
import { ExtensionNotInstalled } from './ExtensionNotInstalled';
import type { ForgeUIRendererProps, CloudOrWorkspaceId, ConfluenceCustomUIProps } from './types';

type EmbeddedMacroRendererProps = Omit<ForgeUIRendererProps, 'extension'> &
	CloudOrWorkspaceId &
	ConfluenceCustomUIProps & {
		extensionId: string;
	};

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

const useExtensionList = conditionalHooksFactory(
	() => fg('forge-ui-use-relay-extension-list-hook'),
	useExtensionListRelay,
	useExtensionListApollo,
);

/**
 * Resolves an embedded macro extension by id via useExtensionList and renders ForgeUIRenderer with the full extension.
 * Callers pass extensionId and context; this component fetches the extension and renders nothing while loading,
 * ExtensionNotInstalled when not found (if validation flag is on), or ForgeUIRenderer with the resolved extension.
 */
const EmbeddedMacroRendererInner = (
	props: EmbeddedMacroRendererProps,
): React.JSX.Element | null => {
	const { extensionId, client, contextIds, loadingComponent, ...rest } = props;

	const { extensions, loading } = useExtensionList({
		client,
		contextIds,
		filter: [
			{
				type: GQLExtensionContextsFilterType.ExtensionType,
				value: ['xen:macro'],
			},
		],
	});

	const extension = extensions?.find((ext) => ext.id === extensionId);
	const queryCompleted = extensions !== undefined;
	const notInstalled =
		queryCompleted && !extension && fg('platform_validate_embedded_macro_installation');

	if (loading || !queryCompleted) {
		return getLoadingComponent(loadingComponent);
	}

	if (notInstalled) {
		return <ExtensionNotInstalled extensionId={extensionId} />;
	}

	if (!extension) {
		return null;
	}

	type ForgeUIRendererPropsWithExtension = React.ComponentProps<typeof ForgeUIRenderer>;
	const forgeRendererProps: ForgeUIRendererPropsWithExtension = {
		...rest,
		client,
		contextIds,
		extension,
		loadingComponent,
		isEmbeddedMacro: true,
	};
	return <ForgeUIRenderer {...forgeRendererProps} />;
};

// This wrapper is required to use the Relay environment provider for the useExtensionListRelay hook.
// Once Confluence has fully migrated to Relay, we can remove this wrapper and defer to their Relay environment
export const EmbeddedMacroRenderer = (props: EmbeddedMacroRendererProps) => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<EmbeddedMacroRendererInner {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
};
